import { Box } from '@mui/material';
import { alpha, lighten } from '@mui/material/styles';

type Props = {
  /** Text shown in the pill. Rendered uppercase. */
  label: string;
  /**
   * Base hue. The fill, ring, dot and text are all derived from this single
   * color, so a caller only has to know one value per status.
   *
   * Must be a color `alpha()` can parse — a hex or rgb() string. MUI keyword
   * shorthands like `'default'` throw.
   */
  color: string;
  /** Fade the dot in and out, for states where work is still in flight. */
  pulse?: boolean;
};

/**
 * Status pill: 12% tinted fill, a 1px ring in the same hue, a solid dot and an
 * uppercase label. Replaces the solid-fill Chip, which rendered every row as a
 * saturated block of color and so read as a warning even when nothing was wrong.
 *
 * Takes a color rather than a named tone on purpose. Games and clips have
 * separate, non-overlapping status vocabularies (3 values vs 8) with their own
 * established hues; only the shape is shared, so mapping status -> color stays
 * with each domain and this component stays free of either enum.
 */
const StatusBadge = ({ label, color, pulse = false }: Props) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      height: 22,
      // Tighter on the right: the dot already provides optical padding on the left.
      padding: '0 9px 0 8px',
      borderRadius: '100px',
      backgroundColor: alpha(color, 0.12),
      // An inset shadow rather than a border, so the ring costs no layout size
      // and the pill height stays exactly 22px.
      boxShadow: `inset 0 0 0 1px ${alpha(color, 0.32)}`,
      '@keyframes statusBadgePulse': {
        '0%, 100%': { opacity: 1 },
        '50%': { opacity: 0.3 },
      },
    }}
  >
    <Box
      component="span"
      sx={{
        width: 6,
        height: 6,
        flex: 'none',
        borderRadius: '50%',
        backgroundColor: color,
        ...(pulse && { animation: 'statusBadgePulse 1.6s ease-in-out infinite' }),
      }}
    />
    <Box
      component="span"
      sx={{
        fontSize: 10,
        fontWeight: 500,
        letterSpacing: '0.8px',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        // The dot keeps the exact source hue; the label is lightened off it so
        // dark hues stay legible at 10px on a dark row. grey[700] and
        // purple[700] are both well under 3:1 against the row background raw.
        color: lighten(color, 0.3),
      }}
    >
      {label}
    </Box>
  </Box>
);

export default StatusBadge;
